import axios from 'axios';
import {isTokenTrusted, clearSession} from '../utils/tokenGuard';

const baseURL = process.env.REACT_APP_API_URL || '/api';

const axiosInstance = axios.create({
  baseURL,
  timeout: 10000,
});

function killSession(): void {
  clearSession();
  window.location.replace('/expired');
}

/**
 * REQUEST interceptor — pre-flight token check.
 *
 * Before every request, re-read the token from localStorage (never cached)
 * and validate it client-side. If it is missing, malformed, or expired
 * we abort the request immediately and redirect — no round-trip to the server.
 *
 * We only attach the token when one exists; public endpoints (/guest-auth/bind)
 * are still allowed through without a token.
 */
axiosInstance.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('guest_token');

    if (token) {
      if (!isTokenTrusted(token)) {
        // Token exists but is already invalid/expired — kill the session now
        killSession();
        return Promise.reject(new axios.CanceledError('Session expired'));
      }
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => Promise.reject(error),
);

/**
 * RESPONSE interceptor — server-side rejection.
 *
 * 401 — token rejected by the server (invalid signature, revoked, etc.)
 *       Always fatal: clear session and redirect.
 *
 * 403 — forbidden. We only treat this as a session kill if there IS an
 *       active session in localStorage. Without a session it means the
 *       caller (e.g. the /auth bind page) is handling a pre-auth 403
 *       (device already bound) and must be allowed to handle it itself.
 */
axiosInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    const status     = error.response?.status;
    const hasSession = isTokenTrusted(localStorage.getItem('guest_token'));

    if (status === 401 || (status === 403 && hasSession)) {
      killSession();
    }

    return Promise.reject(error);
  },
);

export default axiosInstance;
